/**
 * Eine **berechnete Marker-Form** als SVG je Stück — dieselbe Rechnung wie
 * `strokePath` im Marker-Labor (`tools/marker-lab`), in em statt px.
 *
 * Die Pinnwand zeichnet ihre Marker weiter aus den festen Kappen in
 * `TextNode.scss`. Eine berechnete Form trägt nur, wessen Palette eine
 * mitbringt (`CanvasTextColorPalette.markerShape`) — bisher der Projekttitel
 * (gewünscht 2026-09-26).
 *
 * **Warum ein SVG je Stück:** Die festen Kappen legen eine Mitte dazwischen,
 * die auf die Länge des Strichs gestreckt wird — Wellen und Streifen würden
 * mitgedehnt. Hier kennt die Form ihre (geschätzte) Länge und legt beides in
 * em hinein. Liegt die Schätzung daneben, dehnt `preserveAspectRatio="none"`
 * das Ganze ein wenig; bei diesen feinen Wellen sieht man das nicht.
 *
 * Bewusst ohne React-Bezug, wie die übrigen `utils/`.
 */

/** Die Einstellungen einer Form, alle Längen in em — wie das JSON des Labors. */
export interface CanvasTextMarkerShapeSpec {
  height: number
  overhang: number
  /** Höhenunterschied zwischen Anfang und Ende; die Richtung ist zufällig. */
  tilt: number
  opacity: number
  wobbleAmp: number
  /** Länge einer Welle (Labor: `wobbleFreq` × 2,2). */
  waveLength: number
  rough: number
  /** Die Form beider Enden. */
  cap: { style: 'pinsel' | 'glatt'; depth: number; count: number; irr: number }
  /** Trockenpinsel: helle Streifen längs des Strichs. */
  streaks: { count: number; strength: number; width: number; length: number }
  /** Wie stark Überstand, Neigung und Enden je Markerstrich streuen. */
  variation: number
}

/**
 * Wie hoch die Ebene ist, auf der die Form liegt: Band plus Luft für
 * Neigung, Welle und Rauheit. Muss zu `.text-node__marker--shaped` passen.
 */
export const CANVAS_TEXT_MARKER_BOX = 1.6

/** Durchschnittliche Zeichenbreite, um die Länge eines Stücks zu schätzen. */
const CHAR_EM = 0.55

/** Einheiten je em im SVG. */
const UNIT = 100

export interface CanvasTextMarkerShapeInput {
  /** Die Saat aus der Formnummer — gleiche Saat, gleiche Form. */
  seed: number
  /** Der Text des Stücks — daraus die geschätzte Länge. */
  text: string
  /** Die GEMESSENE Länge in em, wo sie bekannt ist (`TitleMarkerLayer`) — dann gilt sie statt der Schätzung. */
  lengthEm?: number
  /** Ob hier der Markerstrich beginnt bzw. endet — nur dort Kappe und Überstand. */
  start: boolean
  end: boolean
  /** Ein Strich aus mehreren Stücken liegt gerade — eine Neigung finge in jedem neu an. */
  straight: boolean
}

export interface CanvasTextMarkerShape {
  /** Die Maske als `url("data:…")` für `--marker-shape`. */
  mask: string
  /** Wie weit die Ebene links und rechts über den Text ragt, in em. */
  startOverhang: number
  endOverhang: number
}

/** mulberry32 — dieselbe Folge wie im Labor. */
function random(seed: number): () => number {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = a
    t = Math.imul(t ^ (t >>> 15), t | 1)
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61)
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/** Die geschätzte Länge eines Stücks in em — mindestens ein Zeichen. */
export function canvasTextMarkerLength(text: string): number {
  return Math.max(1, Array.from(text).length) * CHAR_EM
}

/**
 * Der Außenrand eines Endes: Punkte [dx, t] von oben (t = 0) nach unten
 * (t = 1); dx 0 ist ganz außen, negativ weicht nach innen zurück.
 */
function capOutline(cap: CanvasTextMarkerShapeSpec['cap'], depth: number, r: () => number): [number, number][] {
  if (cap.style === 'glatt') return [[0, 0], [0, 1]]
  // Pinsel: Borsten mit flachen Spitzen, unterschiedlich lang, dazwischen Kerben.
  const points: [number, number][] = []
  const bands = cap.count
  const lengths = Array.from({ length: bands }, () => -depth * Math.pow(r(), 1.4) * (0.2 + 0.8 * cap.irr))
  for (let b = 0; b < bands; b += 1) {
    const a = b / bands
    const e = (b + 1) / bands
    const w = e - a
    const length = lengths[b]!
    if (b === 0) points.push([length - depth * 0.15, 0])
    points.push([length, a + w * 0.2])
    points.push([length + depth * 0.04, a + w * 0.5])
    points.push([length, a + w * 0.8])
    const next = b + 1 < bands ? lengths[b + 1]! : length
    points.push([Math.min(length, next) - depth * (0.25 + 0.35 * r()), e])
  }
  points[points.length - 1]![1] = 1
  return points
}

const cache = new Map<string, CanvasTextMarkerShape>()

/**
 * Die Form eines Stücks. Fest für dieselbe Eingabe — neu zeichnen,
 * verschieben oder woanders anzeigen ändert sie nicht.
 *
 * @example
 * const { mask, startOverhang, endOverhang } = canvasTextMarkerShape(PROJECT_TITLE_MARKER,
 *   { seed: 42, text: 'Urlaubsliste', start: true, end: true, straight: false })
 */
export function canvasTextMarkerShape(spec: CanvasTextMarkerShapeSpec, input: CanvasTextMarkerShapeInput): CanvasTextMarkerShape {
  // Gemessen auf 0,05 em gerundet: genau genug, und der Zwischenspeicher bleibt klein.
  const length = input.lengthEm !== undefined ? Math.max(0.1, Math.round(input.lengthEm * 20) / 20) : canvasTextMarkerLength(input.text)
  const key = `${JSON.stringify(spec)}|${input.seed}|${length}|${+input.start}${+input.end}${+input.straight}`
  const cached = cache.get(key)
  if (cached) return cached

  const r = random(input.seed)
  const vary = (x: number) => x * (1 + (r() - 0.5) * 2 * spec.variation * 0.6)

  const overhang = Math.max(0, vary(spec.overhang))
  const startOverhang = input.start ? overhang : 0
  const endOverhang = input.end ? overhang : 0
  const tiltDirection = r() < 0.5 ? -1 : 1
  const tilt = input.straight ? 0 : vary(spec.tilt) * tiltDirection
  const phaseTop = r() * Math.PI * 2
  const phaseBottom = r() * Math.PI * 2
  const phaseFine = r() * Math.PI * 2
  const startCap = input.start ? capOutline(spec.cap, vary(spec.cap.depth), r) : [[0, 0], [0, 1]] as [number, number][]
  const endCap = input.end ? capOutline(spec.cap, vary(spec.cap.depth), r) : [[0, 0], [0, 1]] as [number, number][]

  const width = length + startOverhang + endOverhang
  const middle = CANVAS_TEXT_MARKER_BOX / 2

  /*
    Wo ein Stück an ein anderes stößt (ein fettes Wort im Markerstrich), müssen
    beide Kanten auf derselben Höhe ankommen — sonst stünde dort eine Stufe.
    Welle und Rauheit laufen darum zur Naht hin aus.
  */
  const fade = (x: number) => {
    const reach = 0.3
    const left = input.start ? 1 : Math.min(1, x / reach)
    const right = input.end ? 1 : Math.min(1, (width - x) / reach)
    return Math.max(0, Math.min(left, right))
  }
  const edge = (x: number, top: boolean) => {
    const base = middle + (top ? -spec.height / 2 : spec.height / 2) + tilt * (x / width - 0.5)
    const wave = spec.wobbleAmp * (
      Math.sin((x / spec.waveLength) * Math.PI * 2 + (top ? phaseTop : phaseBottom)) * 0.75
      + Math.sin((x / (spec.waveLength * 0.37)) * Math.PI * 2 + phaseFine) * 0.25
    )
    const rough = (r() - 0.5) * 2 * spec.rough
    return base + (wave + rough) * fade(x)
  }
  const lerp = (a: number, b: number, t: number) => a + (b - a) * t

  const points: [number, number][] = []
  const step = 0.12
  // Oben von links nach rechts, das Ende hinunter, unten zurück, den Anfang hinauf.
  const x0 = -startCap[0]![0]
  const x1 = width + endCap[0]![0]
  for (let x = x0; x < x1; x += step) points.push([x, edge(x, true)])
  const endTop = edge(width, true)
  const endBottom = edge(width, false)
  for (const [dx, t] of endCap) points.push([width + dx, lerp(endTop, endBottom, t)])
  const bx1 = width + endCap[endCap.length - 1]![0]
  const bx0 = -startCap[startCap.length - 1]![0]
  for (let x = bx1; x > bx0; x -= step) points.push([x, edge(x, false)])
  const startTop = edge(0, true)
  const startBottom = edge(0, false)
  for (let i = startCap.length - 1; i >= 0; i -= 1) {
    const [dx, t] = startCap[i]!
    points.push([-dx, lerp(startTop, startBottom, t)])
  }

  const n = (value: number) => Math.round(value * UNIT)
  const d = 'M' + points.map(([x, y]) => `${n(x)} ${n(y)}`).join('L') + 'Z'

  // Trockenpinsel: Streifen, an denen weniger Farbe liegt — ausgespart über eine Maske im SVG.
  const streaks: string[] = []
  for (let i = 0; i < spec.streaks.count; i += 1) {
    const y = middle + (r() - 0.5) * spec.height * 0.8
    const streakLength = width * spec.streaks.length * (0.5 + 0.5 * r())
    const from = r() < 0.5 ? width - streakLength : r() * (width - streakLength)
    const strokeWidth = spec.streaks.width * (0.6 + 0.8 * r())
    streaks.push(`<line x1='${n(from)}' y1='${n(y)}' x2='${n(from + streakLength)}' y2='${n(y)}' stroke='black' stroke-opacity='${spec.streaks.strength}' stroke-width='${n(strokeWidth)}' stroke-linecap='round'/>`)
  }

  const viewBox = `0 0 ${n(width)} ${n(CANVAS_TEXT_MARKER_BOX)}`
  const body = streaks.length > 0 && spec.streaks.strength > 0
    ? `<mask id='m' maskUnits='userSpaceOnUse' x='-50' y='0' width='${n(width) + 100}' height='${n(CANVAS_TEXT_MARKER_BOX)}'><rect x='-50' width='${n(width) + 100}' height='${n(CANVAS_TEXT_MARKER_BOX)}' fill='white'/>${streaks.join('')}</mask><path d='${d}' mask='url(#m)'/>`
    : `<path d='${d}'/>`
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' viewBox='${viewBox}' preserveAspectRatio='none'>${body}</svg>`

  const shape: CanvasTextMarkerShape = {
    mask: `url("data:image/svg+xml,${encodeURIComponent(svg)}")`,
    startOverhang: Math.round(startOverhang * 1000) / 1000,
    endOverhang: Math.round(endOverhang * 1000) / 1000,
  }
  // Jeder Anschlag rechnet alle Marker des Titels neu.
  if (cache.size > 500) cache.clear()
  cache.set(key, shape)
  return shape
}
